import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "react-query";
import { useAuth } from "utilities/auth";
import { useToast } from "components/Toast";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { FileText, X, ExternalLink } from "lucide-react";

/**
 * Partner Agent T&C acceptance modal.
 *
 * Controlled component — caller owns the `open` flag. Fires `onAccept` after
 * successful PUT /users/:uid acceptance so the caller can retry the gated
 * action (e.g. creating a client, saving an application edit). Always-
 * dismissible via Cancel / Escape; dismissal does NOT count as acceptance.
 *
 * The component invalidates the ["user", { uid }] React Query entry so that
 * `useAuth()`'s merged user object reflects the new acceptance state on the
 * next render.
 */
export default function PartnerAgentTermsModal({ open, onClose, onAccept }) {
  const auth = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const uid = auth.user?.uid || auth.user?.id;

  const handleAccept = async () => {
    if (!agreed || !uid) return;
    setSaving(true);
    try {
      await makeApiRequest(
        "put",
        apiRoutes.updateUserProfile(uid),
        auth.user,
        auth.user?.companyId || "",
        { acceptedPartnerAgentTerms: true }
      );

      // Refresh merged user — useAuth reads ["user", { uid }]
      await queryClient.invalidateQueries(["user", { uid }]);

      toast({
        status: "success",
        title: "Terms accepted",
        description: "Thanks for agreeing. Proceeding…",
      });

      setAgreed(false);
      if (typeof onAccept === "function") onAccept();
      onClose?.();
    } catch (err) {
      console.error("[PartnerAgentTermsModal] Accept failed", err);
      toast({
        status: "destructive",
        title: "Failed to record acceptance",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={saving ? undefined : onClose}
    >
      <div
        className="surface rounded-xl ring-1 ring-border p-6 w-full max-w-2xl shadow-lg max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-brand/20 rounded-lg">
              <FileText className="w-5 h-5 text-brand" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">Partner Agent Terms</h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                One-time acknowledgement before you create or edit client
                applications.
              </p>
            </div>
          </div>
          <button
            className="btn btn-ghost h-8 w-8 p-0"
            onClick={onClose}
            aria-label="Close"
            disabled={saving}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Placeholder callout */}
        <div className="shrink-0 rounded-lg ring-1 ring-warning/30 bg-warning/5 px-4 py-3 mb-3 text-xs text-muted-foreground">
          Draft language — final terms will be prepared by counsel. Accepting
          now confirms your agreement to this version (v1) and creates an
          audit record tied to your user profile.
        </div>

        {/* Summary */}
        <div className="flex-1 overflow-y-auto -mx-1 px-1 mb-3">
          <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-2">
            <li>
              You are an <strong>authorized agent</strong> of each merchant
              (Client) whose information you submit, and you have their consent
              to do so.
            </li>
            <li>
              The information you submit is, to the best of your knowledge,
              accurate and complete.
            </li>
            <li>
              Sound Commerce is a data intermediary; KYC / AML / underwriting
              decisions are made by the downstream payment processor, not by
              Sound Commerce.
            </li>
            <li>
              Sound Commerce will record the <strong>date, user agent,
              IP address, and terms version</strong> of your acceptance as
              part of your user profile.
            </li>
            <li>
              These terms may be updated; material changes will re-prompt you
              before you can continue submitting Client data.
            </li>
          </ul>

          <div className="mt-4 text-sm">
            <Link
              to="/partner-agent-terms"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-brand hover:underline"
            >
              Read the full terms
              <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>

        {/* Agreement + actions */}
        <div className="shrink-0 pt-3 border-t border-border space-y-3">
          <label className="flex items-start gap-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-border text-brand focus:ring-brand"
              disabled={saving}
            />
            <span>
              I agree to the Partner Agent Terms (v1) on behalf of myself and
              confirm I am authorized to act on behalf of each Client I will
              submit.
            </span>
          </label>
          <div className="flex gap-2">
            <button
              className="btn btn-ghost flex-1"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary flex-1"
              onClick={handleAccept}
              disabled={!agreed || saving}
            >
              {saving ? "Saving…" : "Accept & Continue"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
